/**
 * Session type is the one product menu choice. It sets the package
 * (clock face, audience chrome, roster expectations). It does not delete
 * keyframes, roleplay, or anything else already on the board.
 *
 * Locked packages: Whiteboard · Walkthrough · Exercise.
 * Legacy ids (huddle, tabletop, …) normalize on read.
 *
 * Status (Draft / Ready / Live / Done) is system-managed — see SCENARIO_SESSION_MODEL.md.
 */

export const SESSION_TYPE_IDS = ['whiteboard', 'walkthrough', 'exercise'] as const

export type SessionTypeId = (typeof SESSION_TYPE_IDS)[number]

/** Wire lifecycle values until renamed to draft/ready/live/done. */
export type WireLifecycle = 'planned' | 'running' | 'completed'

/** User-facing Status. */
export type SessionStatus = 'draft' | 'published' | 'live' | 'done'

export type SessionTypeProfile = {
  id: SessionTypeId
  label: string
  blurb: string
  palette: 'easy' | 'default' | 'everything'
  /** Story clock + timeline package. */
  storyClock: boolean
  /** Shared timed run (Exercise). */
  sharedRun: boolean
  /** `minimal` keeps name, size, description, and Show on board (From/Until). */
  inspect?: 'minimal' | 'standard'
  rolePlay?: boolean
  comments?: boolean
  tornado?: boolean
  findAssets?: boolean
  visibilityKeyframes?: boolean
  waypoints?: boolean
  roadStatus?: boolean
  showPlumes?: boolean
}

/**
 * Package knobs: omit = available; `false` = off for this type.
 * Permissions never widen the package — they only narrow who may use an offered knob.
 */
export const SESSION_TYPES: readonly SessionTypeProfile[] = [
  {
    id: 'whiteboard',
    label: 'Whiteboard',
    blurb: 'An open board. Drop objects, discuss, share, or invite people.',
    palette: 'everything',
    storyClock: false,
    sharedRun: false,
    // No story clock → no timed show/hide, keyframing, or asset linking UI.
    // Tornado and hurricane stay available as one static wind slice. Path tools appear with a clock.
    findAssets: false,
    visibilityKeyframes: false,
    waypoints: false,
    roadStatus: false,
    rolePlay: false,
    comments: true,
    showPlumes: true,
  },
  {
    id: 'walkthrough',
    label: 'Walkthrough',
    blurb: 'Prepared scene with a timeline.',
    palette: 'everything',
    storyClock: true,
    sharedRun: false,
    findAssets: false,
    visibilityKeyframes: true,
    waypoints: true,
    roadStatus: false,
    rolePlay: false,
    comments: true,
    showPlumes: true,
  },
  {
    id: 'exercise',
    label: 'Exercise',
    blurb: 'Timed story with named people and a shared Run.',
    palette: 'everything',
    storyClock: true,
    sharedRun: true,
    tornado: true,
    findAssets: true,
    visibilityKeyframes: true,
    waypoints: true,
    roadStatus: true,
    rolePlay: true,
    comments: true,
    showPlumes: true,
  },
]

/** Map legacy POC ids onto the three locked packages. */
const LEGACY_SESSION_TYPE: Record<string, SessionTypeId> = {
  huddle: 'whiteboard',
  whiteboard: 'whiteboard',
  walkthrough: 'walkthrough',
  'incident-review': 'walkthrough',
  tabletop: 'exercise',
  'deep-dive': 'exercise',
  exercise: 'exercise',
}

export function normalizeSessionType(id: string | undefined | null): SessionTypeId | undefined {
  if (!id) return undefined
  return LEGACY_SESSION_TYPE[id] ?? (SESSION_TYPE_IDS.includes(id as SessionTypeId) ? (id as SessionTypeId) : undefined)
}

export function sessionTypeById(id: string | undefined | null): SessionTypeProfile | undefined {
  const canonical = normalizeSessionType(id)
  if (!canonical) return undefined
  return SESSION_TYPES.find((row) => row.id === canonical)
}

/** Default draft title for a non-wizard blank copy (`Untitled whiteboard`, …). */
export function defaultUntitledTitle(typeLabel: string): string {
  return `Untitled ${typeLabel.trim().toLowerCase()}`
}

/** Wizard create title (`Eric's Exercise`). Empty first name falls back to `My`. */
export function defaultWizardTitle(firstName: string, typeLabel: string): string {
  const first = firstName.trim() || 'My'
  return `${first}'s ${typeLabel}`
}

/**
 * True for stock blank titles (`Untitled board`, `Untitled whiteboard (2)`, …).
 * Also accepts legacy Title Case (`Untitled Whiteboard`) so old boards still match.
 * Wizard titles (`Eric's Whiteboard`) and custom names return false.
 */
export function isDefaultUntitledTitle(title: string): boolean {
  return /^Untitled (?:board|[Ww]hiteboard|[Ww]alkthrough|[Ee]xercise)(?: \(\d+\))?$/u.test(
    title.trim(),
  )
}

export function typeOffersStoryClock(id: string | undefined | null): boolean {
  return sessionTypeById(id)?.storyClock === true
}

export function typeOffersSharedRun(id: string | undefined | null): boolean {
  return sessionTypeById(id)?.sharedRun === true
}

/** Card line under the title. Map name is not included. An unset type is left off. */
export function sessionCardSubtitle(
  duration: string,
  sessionType?: string | null,
  lead?: string | null,
): string {
  const type = sessionTypeById(sessionType)?.label
  return [lead, duration, type].filter((part) => Boolean(part)).join(' · ')
}

/**
 * Narrow this list to hide types in Settings. Boards that already use a
 * legacy id still resolve via normalize; the dropdown shows canonical ids.
 */
export const SESSION_TYPE_CEILING: readonly SessionTypeId[] = SESSION_TYPE_IDS

export function offeredSessionTypes(currentId?: string | null): SessionTypeProfile[] {
  const allowed = new Set<string>(SESSION_TYPE_CEILING)
  const canonical = normalizeSessionType(currentId)
  if (canonical) allowed.add(canonical)
  return SESSION_TYPES.filter((row) => allowed.has(row.id))
}

/**
 * Settings / Inspect chrome density from roster seat — not a package override.
 * `expanded` = owner/editor Settings extras (e.g. Palette Options depth).
 * `simple` = narrower Inspect when the type profile sets `inspect: 'minimal'`.
 */
export type SettingsChrome = 'expanded' | 'simple'

export type PackageKnob = keyof Pick<
  SessionTypeProfile,
  | 'rolePlay'
  | 'comments'
  | 'tornado'
  | 'findAssets'
  | 'visibilityKeyframes'
  | 'waypoints'
  | 'roadStatus'
  | 'showPlumes'
>

/**
 * Whether this session type's package offers a knob.
 * `false` on the profile hides it for everyone (including admins).
 * Omitted on the profile ⇒ offered.
 */
export function packageOffers(
  profile: SessionTypeProfile | undefined,
  knob: PackageKnob,
): boolean {
  if (!profile) return true
  return profile[knob] !== false
}

export function studioMinimalInspect(
  profile: SessionTypeProfile | undefined,
  chrome: SettingsChrome,
): boolean {
  return chrome === 'simple' && profile?.inspect === 'minimal'
}

/**
 * Ghosts peek at objects a From/Until window is hiding.
 * Only useful when the open session's type includes a story clock.
 */
export function sessionTypeOffersGhosts(
  profile: SessionTypeProfile | undefined,
): boolean {
  return profile?.storyClock === true
}

/** Published is an explicit trainer mark — not a completeness heuristic. */
export function sessionIsPublished(session: { published?: boolean }): boolean {
  return session.published === true
}

/** Hard gates — disable Publish until these are met. */
export function sessionPublishBlockers(session: {
  title?: string
  sessionType?: string
  maps?: readonly unknown[]
}): string[] {
  const blockers: string[] = []
  if (!normalizeSessionType(session.sessionType)) blockers.push('Choose a session type')
  if (!session.maps || session.maps.length === 0) blockers.push('Offer at least one map')
  return blockers
}

/** Soft tips for tooltips — do not disable Publish. */
export function sessionPublishHints(session: {
  title?: string
  sessionType?: string
  maps?: readonly unknown[]
  objects?: readonly unknown[]
}): string[] {
  const hints = sessionPublishBlockers(session)
  const title = (session.title ?? '').trim()
  if (!title || /^untitled\b/i.test(title)) hints.push('Consider a clearer title')
  return hints
}

/** Map wire lifecycle (+ published) → user Status. */
export function statusFromLifecycle(
  lifecycle: WireLifecycle | string | undefined | null,
  session?: { published?: boolean },
): SessionStatus {
  if (lifecycle === 'running') return 'live'
  if (lifecycle === 'completed') return 'done'
  if (lifecycle === 'planned' || !lifecycle) {
    return session && sessionIsPublished(session) ? 'published' : 'draft'
  }
  return 'draft'
}

export function statusLabel(status: SessionStatus): string {
  switch (status) {
    case 'draft':
      return 'Draft'
    case 'published':
      return 'Published'
    case 'live':
      return 'Live'
    case 'done':
      return 'Done'
  }
}

/** Rail / chip label from wire lifecycle. */
export function lifecycleStatusLabel(
  lifecycle: WireLifecycle | string | undefined | null,
  session?: { published?: boolean },
): string {
  return statusLabel(statusFromLifecycle(lifecycle, session))
}

/**
 * Session-window + story-transport button copy.
 * Title case on strip buttons (not ALL CAPS).
 */
export function sessionWindowLabels(): {
  publish: string
  publishTitle: string
  share: string
  shareTitle: string
  goLive: string
  goLiveTitle: string
  endSession: string
  endSessionTitle: string
  newSession: string
} {
  return {
    publish: 'Publish',
    publishTitle: 'Publish this session',
    share: 'Share',
    shareTitle: 'Link, QR, or invite people',
    goLive: 'Go Live',
    goLiveTitle: 'Go Live for people who have joined',
    endSession: 'End session',
    endSessionTitle: 'End the session and mark it Done',
    newSession: 'New session',
  }
}

export function storyTransportLabels(typeId: string | undefined | null): {
  preview: string
  play: string
  pause: string
  resume: string
  run: string
  stop: string
  runAgain: string
  /** Disabled tip when a participant hovers playback controls they cannot drive. */
  hostControlledTip: string
} {
  const shared = typeOffersSharedRun(typeId)
  return {
    preview: 'Preview',
    play: 'Play',
    pause: 'Pause',
    resume: 'Resume',
    run: shared ? 'Run' : 'Play',
    stop: 'Stop',
    runAgain: shared ? 'Start Run again' : 'Reset',
    hostControlledTip: shared
      ? 'The host controls the run'
      : 'The host controls playback',
  }
}

/** @deprecated Prefer sessionWindowLabels / storyTransportLabels. */
export function sessionStartLabel(typeId: string | undefined | null): {
  button: string
  again: string
  title: string
} {
  const win = sessionWindowLabels()
  const story = storyTransportLabels(typeId)
  return {
    button: win.goLive,
    again: typeOffersSharedRun(typeId) ? story.runAgain : win.newSession,
    title: win.goLiveTitle,
  }
}

/** Passive Focus chrome for watch / passive presence (attendee surface). */
export type PassiveFocusChrome = {
  showLegend: boolean
  /** Trainer may bump legend complexity mid-session (Whiteboard). */
  legendBumpAllowed: boolean
  showClock: boolean
  showTransport: boolean
  showTimeline: boolean
  /** Hide entire right rail + tabs. */
  hideRightRail: boolean
  /** When rail is partially shown, which docks stay. */
  showPeople: boolean
  showSessions: boolean
  showInspect: boolean
  showComments: boolean
  showSettings: boolean
}

export function passiveFocusChrome(typeId: string | undefined | null): PassiveFocusChrome {
  const id = normalizeSessionType(typeId) ?? 'whiteboard'
  if (id === 'whiteboard') {
    return {
      showLegend: true,
      legendBumpAllowed: true,
      showClock: false,
      showTransport: false,
      showTimeline: false,
      hideRightRail: true,
      showPeople: false,
      showSessions: false,
      showInspect: false,
      showComments: true,
      showSettings: false,
    }
  }
  if (id === 'walkthrough') {
    return {
      showLegend: true,
      legendBumpAllowed: false,
      showClock: true,
      showTransport: true,
      showTimeline: true,
      hideRightRail: false,
      showPeople: true,
      showSessions: false,
      showInspect: false,
      showComments: true,
      showSettings: false,
    }
  }
  // Exercise — everything on
  return {
    showLegend: true,
    legendBumpAllowed: false,
    showClock: true,
    showTransport: true,
    showTimeline: true,
    hideRightRail: false,
    showPeople: true,
    showSessions: true,
    showInspect: true,
    showComments: true,
    showSettings: true,
  }
}

/**
 * Whether story transport controls appear for this person on this type.
 * Whiteboard: no. Walkthrough/Exercise: yes when the type offers a story clock.
 */
export function showStoryTransport(opts: {
  typeId: string | undefined | null
  accountBoardMode: 'authoring' | 'following'
  focusMode?: boolean
}): boolean {
  const profile = sessionTypeById(opts.typeId)
  if (!profile?.storyClock) return false
  if (opts.focusMode && opts.accountBoardMode === 'following') {
    return passiveFocusChrome(opts.typeId).showTransport
  }
  return true
}

/** CSS modifiers for Focus mode (see scenarios.css). */
export function focusModeClassNames(opts: {
  focusMode: boolean
  typeId: string | undefined | null
  accountBoardMode: 'authoring' | 'following'
}): string {
  if (!opts.focusMode) return ''
  const parts = ['is-focus']
  const chrome = passiveFocusChrome(opts.typeId)
  const passive = opts.accountBoardMode === 'following'
  if (passive && chrome.hideRightRail) parts.push('is-focus-hide-rail')
  if (passive && !chrome.showClock) parts.push('is-focus-hide-clock')
  if (passive && chrome.showPeople && !chrome.hideRightRail) parts.push('is-focus-people-only')
  return parts.join(' ')
}
